import { useEffect } from "react";
import { SITE } from "@/config/site";
import { OG_DEFAULT } from "@/config/seoData";

const upsert = (attr, key, content) => {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Meta/link keys the Seo component fully owns; reset before applying so a route
// change never leaves a stale value (e.g. a prior page's canonical or og:url) behind.
const OWNED_META = [
  ["name", "description"],
  ["name", "robots"],
  ["name", "twitter:card"],
  ["name", "twitter:title"],
  ["name", "twitter:description"],
  ["name", "twitter:image"],
  ["property", "og:title"],
  ["property", "og:type"],
  ["property", "og:site_name"],
  ["property", "og:description"],
  ["property", "og:url"],
  ["property", "og:image"],
  ["property", "og:image:width"],
  ["property", "og:image:height"],
  ["property", "og:image:alt"],
];

const removeMeta = (attr, key) => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
};
const removeLink = (rel) => {
  const el = document.head.querySelector(`link[rel="${rel}"]`);
  if (el) el.remove();
};

const Seo = ({
  title,
  description,
  canonicalPath,
  ogImage = OG_DEFAULT,
  ogType = "website",
  jsonLd = [],
  noindex = false,
}) => {
  useEffect(() => {
    // Reset all owned tags so no stale value carries over from the previous route.
    OWNED_META.forEach(([attr, key]) => removeMeta(attr, key));
    removeLink("canonical");

    const fullTitle = title
      ? title.includes(SITE.name)
        ? title
        : `${title} — ${SITE.name}`
      : `${SITE.name} — ${SITE.tagline}`;
    document.title = fullTitle;

    if (description) {
      upsert("name", "description", description);
      upsert("property", "og:description", description);
      upsert("name", "twitter:description", description);
    }

    upsert("property", "og:title", fullTitle);
    upsert("property", "og:type", ogType);
    upsert("property", "og:site_name", SITE.name);
    upsert("name", "twitter:card", "summary_large_image");
    upsert("name", "twitter:title", fullTitle);

    if (ogImage) {
      upsert("property", "og:image", ogImage);
      upsert("property", "og:image:width", "1200");
      upsert("property", "og:image:height", "630");
      upsert("property", "og:image:alt", `${fullTitle} — ${SITE.name}`);
      upsert("name", "twitter:image", ogImage);
    }

    upsert("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    if (noindex || !canonicalPath) {
      // Noindex / unknown-canonical pages must not keep a stale canonical or og:url.
      removeLink("canonical");
      removeMeta("property", "og:url");
    } else {
      const canonicalHref = `${SITE.domain}${canonicalPath}`;
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalHref);
      upsert("property", "og:url", canonicalHref);
    }

    const scripts = jsonLd.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoJsonld = "true";
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [title, description, canonicalPath, ogImage, ogType, jsonLd, noindex]);
  return null;
};

export default Seo;
